import React, { useEffect, useMemo } from 'react';

import { supabase } from 'hooks/useAuth';
import { useMemberGroupData } from 'hooks/useMemberGroupData';
import { useToast } from 'hooks/useToast';

import { formatDate } from 'libs/utils';

import { colors, Fonts, spacing } from 'styles/theme';
import { Bumper, NestedCard } from 'styles/shared';
import { Link } from 'react-router-dom';
import { ValueGroup } from './SubmitValues';

interface ValuesResultsProps {
    valuesId: string;
}

interface ShelfLabelProps {
    label: string;
    description: string;
    color: string;
    emoji: string;
}

const ShelfLabel = ({ label, description, color, emoji }: ShelfLabelProps) => {
    return (
        <div
            style={{
                padding: '8px',
                backgroundColor: colors.white,
                borderBottom: `2px solid ${color}`,
                borderRadius: '4px',
                marginBottom: '8px',
            }}
        >
            <Fonts.Medium style={{ color, fontWeight: 'bold' }}>
                {emoji} {label}
            </Fonts.Medium>
            <Bumper $minHeight={spacing.xxxSmall} />
            <Fonts.Small style={{ color: '#666', marginTop: '4px' }}>
                {description}
            </Fonts.Small>
        </div>
    );
};

export const ValuesResults = ({ valuesId }: ValuesResultsProps) => {
    const { values } = useMemberGroupData();
    const { showErrorToast } = useToast();

    const [signedImageUrls, setSignedImageUrls] = React.useState<string[]>([]);

    const selectedValues = useMemo(
        () => values.find((value) => value.id === valuesId),
        [valuesId, values]
    );

    const hasImages =
        selectedValues?.image_urls != null &&
        selectedValues.image_urls.length > 0;

    useEffect(() => {
        const getSignedUrls = async () => {
            const { data, error } = await supabase.storage
                .from('values-images')
                .createSignedUrls(selectedValues?.image_urls ?? [], 3600);

            if (error) {
                showErrorToast(error.message);
                return;
            }
            setSignedImageUrls(data.map((d) => d.signedUrl));
        };

        if (hasImages) {
            getSignedUrls();
        }
    }, [hasImages, selectedValues, showErrorToast]);

    // Same shelf definitions/colors as the SubmitValues ranking step
    const shelfLabels: ShelfLabelProps[] = [
        {
            label: 'Non-negotiable bowl',
            description:
                'Things you hope to guarantee yourself. Use this sparingly.',
            color: colors.pursuitGreen,
            emoji: '💎',
        },
        {
            label: 'Top shelf',
            description:
                'Things to strive hard for. These things will drive most of your career choices.',
            color: colors.pursuitBlue,
            emoji: '🏆',
        },
        {
            label: 'Middle shelf',
            description:
                "Things to avoid total failure at. This shelf is good for the less-important qualities you feel you can't totally neglect.",
            color: colors.pursuitGold,
            emoji: '📚',
        },
        {
            label: 'Bottom shelf',
            description:
                'Things to keep in mind. Nice to have, but not at the expense of anything above it.',
            color: colors.pursuitBrown,
            emoji: '📦',
        },
        {
            label: 'Trash can',
            description:
                "Things to reject. Things you're actively choosing to deny",
            color: colors.pursuitOrange,
            emoji: '🗑️',
        },
    ];

    // Derive ranked groups (ascending by ranking), safe on unknown data
    const rankedGroups = useMemo(() => {
        const raw = selectedValues?.value_groups;
        if (!raw || !Array.isArray(raw)) return [];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return [...raw].sort((a: any, b: any) => {
            const ra = typeof a?.ranking === 'number' ? a.ranking : 9999;
            const rb = typeof b?.ranking === 'number' ? b.ranking : 9999;
            return ra - rb;
        });
    }, [selectedValues]);

    return (
        <>
            <Fonts.Heading5>{`Your Values`}</Fonts.Heading5>
            <Fonts.Medium $color={colors.mountainAsh}>
                {formatDate(new Date(selectedValues?.created_at ?? 0))}
            </Fonts.Medium>
            <Bumper />

            {hasImages ? (
                <>
                    <Fonts.Medium $isOpenSansSemiBold>
                        {`Submitted images:`}
                    </Fonts.Medium>
                    <Bumper $minHeight={spacing.xSmall} />
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns:
                                'repeat(auto-fill, minmax(200px, 1fr))',
                            gap: '16px',
                        }}
                    >
                        {signedImageUrls.map((imageUrl, index) => (
                            <Link key={index} to={imageUrl} rel="noreferrer">
                                <img
                                    src={imageUrl}
                                    alt={`Values upload ${index + 1}`}
                                    style={{
                                        width: '100%',
                                        height: '200px',
                                        objectFit: 'cover',
                                        borderRadius: '8px',
                                        border: '2px solid #ddd',
                                        transition: 'border-color 0.2s ease',
                                    }}
                                />
                            </Link>
                        ))}
                    </div>
                </>
            ) : rankedGroups.length > 0 ? (
                <>
                    <Fonts.Medium $isOpenSansSemiBold>
                        {`Submitted value phrases:`}
                    </Fonts.Medium>
                    <Bumper $minHeight={spacing.xSmall} />

                    {(rankedGroups as unknown as Array<ValueGroup>).map(
                        (group: ValueGroup, index: number) => {
                            const shelf = shelfLabels[index];
                            const frameColor = shelf?.color ?? '#ccc';

                            return (
                                <div
                                    key={`${group?.color ?? 'group'}-${index}`}
                                    style={{
                                        border: `2px solid ${frameColor}`,
                                        borderRadius: '8px',
                                        padding: '8px',
                                        marginBottom: '16px',
                                    }}
                                >
                                    {shelf && <ShelfLabel {...shelf} />}

                                    <NestedCard
                                        style={{
                                            cursor: 'default',
                                            touchAction: 'auto',
                                        }}
                                    >
                                        {/* Phrase only (mirrors SortableItem display) */}
                                        <Fonts.Small $color={colors.darkJungle}>
                                            {group?.phrase ?? ''}
                                        </Fonts.Small>

                                        {/* Optional: show values inline beneath */}
                                        {/* {Array.isArray(group?.values) &&
                                            group.values.length > 0 && (
                                                <>
                                                    <Bumper
                                                        $minHeight={
                                                            spacing.xxxSmall
                                                        }
                                                    />
                                                    <Fonts.Small
                                                        $color={
                                                            colors.mountainAsh
                                                        }
                                                    >
                                                        {group.values.join(
                                                            ', '
                                                        )}
                                                    </Fonts.Small>
                                                </>
                                            )} */}
                                    </NestedCard>
                                </div>
                            );
                        }
                    )}
                </>
            ) : (
                <Fonts.Medium>{`No values data available for this submission.`}</Fonts.Medium>
            )}
        </>
    );
};
